import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
  ViewEncapsulation
} from '@angular/core';
import {
  BasicVsciModule,
  GenericValue,
  LogManager,
  ModelChangeService,
  ModelTransformerService
} from '@visana-web/basic-ui';
import {ClientModel} from 'app/model/client-model';
import {Subscription} from 'rxjs';
import {TranslateModule} from "@ngx-translate/core";
import {NgForOf, NgIf} from "@angular/common";
import {SearchResultEntryComponent} from "./search-result-entry/search-result-entry.component";
import {InfiniteScrollModule} from "ngx-infinite-scroll";


@Component({
  selector: 'app-search-result',
  templateUrl: './search-result.component.html',
  styleUrls: ['./search-result.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  standalone: true,
  imports: [
    BasicVsciModule,
    TranslateModule,
    NgIf,
    NgForOf,
    SearchResultEntryComponent,
    InfiniteScrollModule
  ]
})
export class SearchResultComponent implements OnInit, OnDestroy {

  private clientModel?: ClientModel;

  public searchCriteria?: GenericValue;
  public gatekeepers?: GenericValue[] = undefined;
  private gatekeepersAll?: GenericValue[] = undefined;

  private modelChangeSubscription?: Subscription;

  private readonly ZIP_SEARCHCRITERIA_FIELDNAME = "6";
  private readonly UMKREIS_SEARCHCRITERIA_FIELDNAME = "8";


  pdfLink?: string
  pdfLink_network?: string

  totalHits?: number;

  // sorting
  key = 'name';
  reverse = false;

  sum = 90;
  step = 60;
  throttle = 200;
  scrollDistance = 1.5;
  scrollUpDistance = 2;

  constructor(
    private modelTransformerService: ModelTransformerService,
    private modelChangeService: ModelChangeService<ClientModel>,
    private ref: ChangeDetectorRef,
    private logManager: LogManager) {
  }

  ngOnDestroy(): void {
    if (this.modelChangeSubscription) {
      this.modelChangeSubscription.unsubscribe();
    }
  }

  ngOnInit() {
    if (!this.modelChangeSubscription) {
      this.modelChangeSubscription = this.modelChangeService.observableModel.subscribe(value => {
        this.clientModel = value;
        this.searchCriteria = this.clientModel.searchCriteria as GenericValue;
        if (this.clientModel.searchResult) {
          this.gatekeepersAll = this.modelTransformerService.getModelCollectionGenericValues(this.clientModel.searchResult, 'gateKeepers/gatekeepers');
          if (!this.gatekeepersAll) {
            this.resetList();
            return;
          }
          this.gatekeepers = this.gatekeepersAll.slice(0, this.sum);

          this.filterSearchResult();

          this.sort(this.key);
          this.totalHits = this.gatekeepers.length;
          if (this.totalHits > 0) {
            this.pdfLink = this.modelTransformerService.getModelCollectionValue(this.clientModel.searchResult, 'gateKeepers/relations/relations', 'type', 'gatekeeper', 'uri/string');
            this.pdfLink_network = this.modelTransformerService.getModelCollectionValue(this.clientModel.searchResult, 'gateKeepers/relations/relations', 'type', 'medicalNetwork', 'uri/string');
          }
          this.ref.detectChanges();
        } else {
          this.resetList();
          return;
        }
      });
    }
  }

  private filterSearchResult() {
    if (this.isOnlyZipSelected())
      this.gatekeepers = this.gatekeepers?.filter(arzt => {
        let searchZip = this.searchCriteria?.children?.find(value1 => value1.fieldName == this.ZIP_SEARCHCRITERIA_FIELDNAME)?.value;
        if (this.modelTransformerService.getModelValue(arzt, 'employerPostCode/postalCode')) {
            let employerZip = this.modelTransformerService.getModelValue(arzt, 'employerPostCode/postalCode')
            return searchZip!.includes(employerZip);
          } else {
            let doctorZip = this.modelTransformerService.getModelValue(arzt, 'gatekeeperPostCode/postalCode')
            return searchZip!.includes(doctorZip);
          }
        }
      )
  }

  isOnlyZipSelected() {
    const filterZip = this.searchCriteria?.children?.find(value1 => value1.fieldName == this.ZIP_SEARCHCRITERIA_FIELDNAME);
    const umkreis = this.searchCriteria?.children?.find(value1 => value1.fieldName == this.UMKREIS_SEARCHCRITERIA_FIELDNAME);
    return filterZip?.value && umkreis?.value == undefined;
  }

  private resetList() {
    this.gatekeepers = undefined;
    this.totalHits = undefined;
    // sorting
    this.key = 'name';
    this.reverse = false;
    this.ref.detectChanges();
  }

  onScrollDown() {
    if (!this.gatekeepers) {
      return;
    }
    if (this.gatekeepers.length === this.gatekeepersAll?.length) {
      return;
    }

    this.sum += this.step;
    this.gatekeepers = this.gatekeepersAll?.slice(0, this.sum - 1);
    this.filterSearchResult();
  }

  getSortingCursorClass(): string {
    if (this.totalHits && this.totalHits > 1) {
      return ' cursor-pointer';
    }
    return ' cursor-default';
  }

  getSortingClass(key: string) {
    if ((key === this.key) && (this.totalHits && this.totalHits > 1)) {
      return ' vis-web-imprest-sorted-header';
    }
    return '';
  }


  public sort(key: string) {
    if (this.totalHits && this.totalHits < 2) {
      this.logManager.info('Abort sorting, less than 2 entries: ' + key);
      return;
    }
    this.logManager.info('sorting: ' + key);

    const moveSmaller = this.reverse ? 1 : -1;
    const moveLarger = this.reverse ? -1 : 1;

    if (!this.gatekeepers) {
      return;
    }
    const athis = this;
    if (key === 'name') {
      this.gatekeepers.sort(function (a, b) {
        if (athis.modelTransformerService.getModelValue(a, 'displayName').toLocaleLowerCase().trim() < athis.modelTransformerService.getModelValue(b, 'displayName').toLocaleLowerCase().trim()) {
          return moveSmaller;
        }
        if (athis.modelTransformerService.getModelValue(a, 'displayName').toLocaleLowerCase().trim() > athis.modelTransformerService.getModelValue(b, 'displayName').toLocaleLowerCase().trim()) {
          return moveLarger;
        }
        return 0;
      });
    }

    if (key === 'locality') {
      this.gatekeepers.sort(function (a, b) {
        if (athis.modelTransformerService.getModelValue(a, 'displayLocality').toLocaleLowerCase().trim() < athis.modelTransformerService.getModelValue(b, 'displayLocality').toLocaleLowerCase().trim()) {
          return moveSmaller;
        }
        if (athis.modelTransformerService.getModelValue(a, 'displayLocality').toLocaleLowerCase().trim() > athis.modelTransformerService.getModelValue(b, 'displayLocality').toLocaleLowerCase().trim()) {
          return moveLarger;
        }
        return 0;
      });
    }

    if (key === 'billerType') {
      this.gatekeepers.sort(function (a, b) {
        if (athis.modelTransformerService.getModelValue(a, 'billerTypeDescription').toLocaleLowerCase().trim() < athis.modelTransformerService.getModelValue(b, 'billerTypeDescription').toLocaleLowerCase().trim()) {
          return moveSmaller;
        }
        if (athis.modelTransformerService.getModelValue(a, 'billerTypeDescription').toLocaleLowerCase().trim() > athis.modelTransformerService.getModelValue(b, 'billerTypeDescription').toLocaleLowerCase().trim()) {
          return moveLarger;
        }
        return 0;
      });
    }

    this.key = key;
    this.reverse = !this.reverse;
  }
}
